import { reloadOnce } from './reloadGuard';

/**
 * Recovery for a lazily-loaded chunk that is no longer on the server.
 *
 * The scenario is specific and entirely ordinary: someone opens the app, leaves the tab, a deploy
 * lands, `rsync --delete` removes the old content-hashed chunks, and then they come back and tap
 * «Мои авто». The import for that screen 404s and React unmounts into nothing — the screen simply
 * does not open, with no error a person can act on. Splitting the bundle without handling this
 * turns a background deploy into a broken app for everyone with a tab open.
 *
 * Vite raises `vite:preloadError` for exactly this, which is the one place where reloading the
 * page IS the fix: the new `index.html` names chunks that exist.
 *
 * Once the service worker precaches the build, this fires less often — the SW answers from its
 * own cache and never reaches the missing file. It is still needed: the very first visit after a
 * deploy has no worker in control yet, and a person who declined the update prompt keeps the old
 * page against new server content.
 *
 * The loop guard lives in `reloadGuard.ts` and is shared with the worker-update path, so the two
 * cannot take turns reloading the same tab.
 */
export function installChunkReload(): void {
  window.addEventListener('vite:preloadError', (event) => {
    // Without this Vite rethrows and the failure surfaces as an unhandled rejection instead.
    event.preventDefault();
    // If a reload just happened, `RouteError` takes it from here with a readable explanation.
    reloadOnce();
  });
}

/** True when this error is «the chunk is gone», not «the screen threw». */
export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return (
    /dynamically imported module|Importing a module script failed|Failed to fetch/i.test(
      error.message,
    ) || error.name === 'ChunkLoadError'
  );
}
